import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AuthService } from "../services/auth.service.real";
import { RegistrationComplete } from "../components/auth/RegistrationComplete";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<"verifying" | "success" | "error">(
    "verifying"
  );
  const [error, setError] = useState<string>("");
  const verificationAttempted = useRef(false);

  const verifyEmailMutation = useMutation({
    mutationFn: (token: string) => AuthService.verifyEmail(token),
    onSuccess: (result) => {
      if (result.success) {
        setVerificationStatus("success");
        setError("");
      } else {
        setError("Email verification failed. Please try again.");
        setVerificationStatus("error");
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Email verification failed. Please try again.";
      setError(errorMessage);
      setVerificationStatus("error");
    },
  });

  useEffect(() => {
    const token = searchParams.get("token");
    if (token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyEmailMutation.mutate(token);
    } else if (!token) {
      setError("No verification token found in URL");
      setVerificationStatus("error");
    }
  }, [searchParams, verifyEmailMutation]);

  const handleComplete = () => {
    navigate("/");
  };

  const handleDismissError = () => {
    setError("");
  };

  const handleRetryVerification = () => {
    const token = searchParams.get("token");
    if (token) {
      setVerificationStatus("verifying");
      setError("");
      verificationAttempted.current = false;
      verifyEmailMutation.mutate(token);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden font-arial">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        {/* Dark overlay for better readability */}
        <div className="absolute inset-0 bg-primary-blue"></div>
      </div>

      <div className="relative z-10">
        {verificationStatus === "verifying" && (
          <Card className="w-full max-w-sm bg-white/95 backdrop-blur-sm shadow-2xl border-0 ">
            <CardHeader className="text-center">
              <CardTitle>Verifying your Email...</CardTitle>
              <CardDescription>Please wait while we verify your Email address</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <div className="animate-spin rounded-none h-8 w-8 border-b-2 border-indigo-600"></div>
            </CardContent>
          </Card>
        )}

        {verificationStatus === "success" && <RegistrationComplete onContinue={handleComplete} />}

        {verificationStatus === "error" && (
          <>
            <ErrorMessage
              message={error}
              onDismiss={handleDismissError}
              className="max-w-2xl mb-4"
            />
            <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-2xl border-0 ">
              <CardHeader className="text-center">
                <CardTitle>Verification Failed</CardTitle>
                <CardDescription>
                  There was an issue verifying your email address. Please try again.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <button
                  onClick={handleRetryVerification}
                  className="w-full bg-indigo-blue hover:bg-indigo-darker-blue text-white font-medium-plus py-2 px-4 rounded-xl"
                  disabled={verifyEmailMutation.isPending}
                >
                  {verifyEmailMutation.isPending ? "Retrying..." : "Retry Verification"}
                </button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};
